import { supabase } from '@/lib/supabase';
import { formatChatMessagePreview } from './formatChatMessagePreview';
import { chatKey, filterColumn, targetForKind, targetId } from './chatTarget';
import type { ChatTarget } from './types';

type ChatForPreview = {
  target: ChatTarget;
  last_message_at: string | null;
};

const PREVIEW_CHUNK_SIZE = 50;

function quotePostgrestValue(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/** Enough about the last message to render the row AND decide if it is unread. */
export type LastMessageInfo = {
  preview: string;
  messageId: string;
  /** Sender, so a thread whose newest message is your own never counts as unread. */
  senderId: string;
};

/**
 * Last-message previews for a mixed list of event, department and direct threads.
 *
 * Keyed by chatKey() rather than event id so every kind shares one map. The kinds are
 * queried separately because the `or` filter names a single column, and the loop runs
 * over the kinds actually present rather than a hard-coded list, so a new ChatTarget
 * variant needs no edit here.
 */
export async function fetchLastMessagesForChats(
  chats: ChatForPreview[]
): Promise<Map<string, LastMessageInfo>> {
  const withTimestamp = chats.filter((c) => c.last_message_at);
  if (withTimestamp.length === 0) return new Map();

  const previews = new Map<string, LastMessageInfo>();

  const kinds = [...new Set(withTimestamp.map((c) => c.target.kind))];

  for (const kind of kinds) {
    const ofKind = withTimestamp.filter((c) => c.target.kind === kind);
    if (ofKind.length === 0) continue;

    const column = filterColumn(ofKind[0].target);

    for (let i = 0; i < ofKind.length; i += PREVIEW_CHUNK_SIZE) {
      const chunk = ofKind.slice(i, i + PREVIEW_CHUNK_SIZE);
      const orFilter = chunk
        .map(
          (c) =>
            `and(${column}.eq.${targetId(c.target)},created_at.eq.${quotePostgrestValue(c.last_message_at!)})`
        )
        .join(',');

      const { data, error } = await supabase
        .from('messages_new')
        .select(`id, user_id, ${column}, content, image_url, type, file_name, file_url, sender_name`)
        .or(orFilter);

      if (error) {
        console.error('[fetchLastMessagesForChats] Failed to load previews', error);
        continue;
      }

      for (const row of data ?? []) {
        const rowId = (row as Record<string, unknown>)[column] as string | null;
        if (!rowId) continue;
        previews.set(chatKey(targetForKind(kind, rowId)), {
          preview: formatChatMessagePreview({
            content: row.content as string,
            image_url: row.image_url as string | null,
            type: row.type as string | null,
            file_name: row.file_name as string | null,
            file_url: row.file_url as string | null,
            sender_name: row.sender_name as string | null,
          }),
          messageId: row.id as string,
          senderId: row.user_id as string,
        });
      }
    }
  }

  return previews;
}
